import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css';
import { DateRange } from 'react-date-range';
import { format } from 'date-fns';
import { joinClassNames } from '@utils/joinClassNames';
import { bemClassName } from '@utils/bemClassName';
import { useField, Field } from 'formik';
import { useRef, useState } from 'react';
import { useClickAway } from '@hooks/useClickAway';
import { useMediaQuery } from 'react-responsive';
import { MEDIA_QUERIES } from '@utils/namedConstants';

const getUseFieldProps = (name) => {
  const [field, meta, helpers] = useField(name);
  return {
    field,
    meta,
    helpers,
  };
};

const CalendarIcon = (props) => {
  return (
    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.77777 14.7232H14.2222V4.94542H1.77777V14.7232ZM15.1111 2.27875H13.3333V1.39875C13.3333 0.902754 12.936 0.500977 12.4444 0.500977C11.9529 0.500977 11.5556 0.902754 11.5556 1.39875V2.27875H4.44444V1.39875C4.44444 0.902754 4.04711 0.500977 3.55556 0.500977C3.064 0.500977 2.66667 0.902754 2.66667 1.39875V2.27875H0.888889C0.397333 2.27875 0 2.67609 0 3.16764V15.6121C0 16.1028 0.397333 16.501 0.888889 16.501H15.1111C15.6027 16.501 16 16.1028 16 15.6121V3.16764C16 2.67609 15.6027 2.27875 15.1111 2.27875ZM8.8889 12.9641H12.4445V9.40853H8.8889V12.9641Z"
        fill="#017D7D"
      />
    </svg>
  );
};

const DateRangeField = ({ label, nameStart, nameEnd }) => {
  const [rangeOpen, setRangeOpen] = useState(false);
  const start = getUseFieldProps(nameStart);
  const end = getUseFieldProps(nameEnd);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);

  // Close range picker on click away
  const ref = useClickAway(() => setRangeOpen(false));

  const isMobileScreen = !useMediaQuery({
    query: MEDIA_QUERIES.SMALL,
  });

  const { value: startValue } = start.meta;
  const { value: endValue } = end.meta;
  const dateFormat = 'yyyy-LL-dd';

  // Set ranges object from state values
  const ranges = [
    {
      key: 'selection',
      color: 'var(--ripple-grey-more-lighter)',
      startDate: startValue ? new Date(startValue) : undefined,
      endDate: endValue ? new Date(endValue) : undefined,
    },
  ];

  /**
   * Handler callback function for the DateRange component
   * Formats start and end dates and set's them in state
   *
   * @param item Range item passed from the onChange Event
   */
  const onRangeChange = (item) => {
    if (item.selection.startDate) {
      const startDate = format(item.selection.startDate, dateFormat);
      start.helpers.setValue(startDate);
    }
    if (item.selection.endDate) {
      const endDate = format(item.selection.endDate, dateFormat);
      end.helpers.setValue(endDate);
      // The same start and end date indicates a click rather than a selection - Do not close picker
      if (item.selection.startDate !== item.selection.endDate) {
        setRangeOpen(false);
      }
    }
  };

  /**
   * Handler callback for input date field click
   * Sets range state to open and prevents default datepicker if not on mobile
   *
   * @param event Mouse event
   */
  function onInputClick(event, inputRef) {
    if (!isMobileScreen) {
      event.preventDefault();
    } else {
      if (inputRef.current) {
        inputRef.current.showPicker();
      }
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
    setRangeOpen(true);
  }

  return (
    <div className={`col-xs-12 col-md-6 col-lg-4`}>
      <label className={joinClassNames([bemClassName('find-a-panel', 'search-title'), 'checkboxGroupLabel'])}>{label.trim()}</label>
      <div ref={ref}>
        <div className="dateRangeInputWrapper">
          <div className="dateInputField" onClick={(e) => onInputClick(e, startInputRef)}>
            <CalendarIcon />
            <Field type="date" {...start.field} value={startValue || ''} innerRef={startInputRef} className={'hidden'} />
            <span className="placeholder">{startValue || 'Enter date from'}</span>
          </div>
          <div className="dateInputField" onClick={(e) => onInputClick(e, endInputRef)} ref={endInputRef}>
            <CalendarIcon />
            <Field type="date" {...end.field} value={endValue || ''} innerRef={endInputRef} className={'hidden'} />
            <span className="placeholder">{endValue || 'Enter date to'}</span>
          </div>
        </div>
        {rangeOpen && !isMobileScreen && (
          <div className="dateRangeFieldWrapper">
            <DateRange editableDateInputs={true} showDateDisplay={false} onChange={onRangeChange} moveRangeOnFirstSelection={false} ranges={ranges} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangeField;
